// src/lib/privacy/dataDeletion.ts

import { db } from '../../firebase/config';
import { 
  collection, 
  getDocs, 
  getDoc, 
  doc, 
  deleteDoc, 
  updateDoc, 
  query, 
  where 
} from 'firebase/firestore';
import { Timestamp } from 'firebase/firestore';
import { DELETABLE_COLLECTIONS, RETAINED_DATA } from '../../types/privacyRequest';

interface DeletionResult {
  deletedCollections: string[];
  deletionExceptions: string[];
  softDeleted: boolean;
}

/**
 * Deletes user data with legal retention exceptions
 * Some data must be retained for legal/compliance reasons
 */
export async function deleteUserData(
  userId: string,
  softDeleteOnly: boolean = false
): Promise<DeletionResult> {
  const deletedCollections: string[] = [];
  const deletionExceptions: string[] = [];
  let softDeleted = false;
  
  // Delete user profile (with exception for legal retention)
  try {
    const userDocRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userDocRef);
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      
      // Check if user has financial records that must be retained
      // For now, we'll soft delete by marking as deleted
      if (softDeleteOnly) {
        await updateDoc(userDocRef, {
          isDeleted: true,
          deletedAt: Timestamp.now(),
          email: `deleted_${Date.now()}@deleted.local`,
          displayName: '[Deleted User]'
        });
        softDeleted = true;
        deletedCollections.push('users (soft delete)');
      } else {
        await deleteDoc(userDocRef);
        deletedCollections.push('users');
      }
    }
  } catch (error: any) {
    deletionExceptions.push(`Error deleting user profile: ${error.message}`);
  }
  
  // Delete client/business data
  try {
    const clientsRef = collection(db, 'clients');
    const clientQuery = query(clientsRef, where('userId', '==', userId));
    const clientSnapshot = await getDocs(clientQuery);
    
    for (const clientDoc of clientSnapshot.docs) {
      if (softDeleteOnly) {
        await updateDoc(clientDoc.ref, {
          isDeleted: true,
          deletedAt: Timestamp.now()
        });
        softDeleted = true;
      } else {
        await deleteDoc(clientDoc.ref);
      }
    }
    if (!clientSnapshot.empty) {
      deletedCollections.push('clients' + (softDeleteOnly ? ' (soft delete)' : ''));
    }
  } catch (error: any) {
    deletionExceptions.push(`Error deleting client data: ${error.message}`);
  }
  
  // Delete tasks (but check for financial/legal retention)
  try {
    const tasksRef = collection(db, 'tasks');
    const tasksQuery = query(tasksRef, where('clientId', '==', userId));
    const tasksSnapshot = await getDocs(tasksQuery);
    
    for (const taskDoc of tasksSnapshot.docs) {
      const taskData = taskDoc.data();
      
      // Check if task is related to financial/legal matters that must be retained
      const isFinancialTask = taskData.category === 'financial' || 
                              taskData.category === 'tax' ||
                              taskData.title?.toLowerCase().includes('tax') ||
                              taskData.title?.toLowerCase().includes('financial');
      
      if (isFinancialTask && !softDeleteOnly) {
        deletionExceptions.push(`Task "${taskData.title}" cannot be deleted (financial record retention required)`);
        continue;
      }
      
      if (softDeleteOnly || isFinancialTask) {
        await updateDoc(taskDoc.ref, {
          isDeleted: true,
          deletedAt: Timestamp.now()
        });
        softDeleted = true;
      } else {
        await deleteDoc(taskDoc.ref);
      }
    }
    if (!tasksSnapshot.empty) {
      deletedCollections.push('tasks' + (softDeleteOnly ? ' (soft delete)' : ''));
    }
  } catch (error: any) {
    deletionExceptions.push(`Error deleting tasks: ${error.message}`);
  }
  
  // Delete documents (but check for tax/financial documents)
  try {
    const documentsRef = collection(db, 'documents');
    const documentsQuery = query(documentsRef, where('ownerId', '==', userId));
    const documentsSnapshot = await getDocs(documentsQuery);
    
    for (const docDoc of documentsSnapshot.docs) {
      const docData = docDoc.data();
      
      // Check if document is a tax or financial document
      const isTaxOrFinancialDoc = docData.type === 'tax' || 
                                   docData.type === 'financial' ||
                                   docData.fileName?.toLowerCase().includes('tax') ||
                                   docData.fileName?.toLowerCase().includes('w-2') ||
                                   docData.fileName?.toLowerCase().includes('1099');
      
      if (isTaxOrFinancialDoc && !softDeleteOnly) {
        deletionExceptions.push(`Document "${docData.fileName}" cannot be deleted (tax document retention required for ${RETAINED_DATA.taxDocuments} years)`);
        continue;
      }
      
      if (softDeleteOnly || isTaxOrFinancialDoc) {
        await updateDoc(docDoc.ref, {
          isDeleted: true,
          deletedAt: Timestamp.now()
        });
        softDeleted = true;
      } else {
        await deleteDoc(docDoc.ref);
      }
    }
    if (!documentsSnapshot.empty) {
      deletedCollections.push('documents' + (softDeleteOnly ? ' (soft delete)' : ''));
    }
  } catch (error: any) {
    deletionExceptions.push(`Error deleting documents: ${error.message}`);
  }
  
  // Entity formation drafts can be deleted
  try {
    const draftsRef = collection(db, 'intakeEntityFormationDrafts');
    const draftsQuery = query(draftsRef, where('createdByUid', '==', userId));
    const draftsSnapshot = await getDocs(draftsQuery);
    
    for (const draftDoc of draftsSnapshot.docs) {
      if (softDeleteOnly) {
        await updateDoc(draftDoc.ref, {
          isDeleted: true,
          deletedAt: Timestamp.now()
        });
        softDeleted = true;
      } else {
        await deleteDoc(draftDoc.ref);
      }
    }
    if (!draftsSnapshot.empty) {
      deletedCollections.push('entityFormationDrafts' + (softDeleteOnly ? ' (soft delete)' : ''));
    }
  } catch (error: any) {
    deletionExceptions.push(`Error deleting entity formation drafts: ${error.message}`);
  }
  
  // Note: Subscriptions and audit logs should NOT be deleted for legal/compliance reasons
  // They are retained per financial records retention requirements
  deletionExceptions.push('Subscriptions data retained for financial records (7 years)');
  deletionExceptions.push('Audit logs retained for compliance (6 years)');
  
  return {
    deletedCollections,
    deletionExceptions,
    softDeleted
  };
}
